import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type {
  ProfileInsert,
  CoachInsert,
  OfferInsert,
  ResourceInsert,
} from "../../../drizzle/schema";

// ==========================================
// PROFILE MANAGEMENT
// ==========================================

const updateCoachDetailsSchema = z.object({
  id: z.string(),
  data: z.object({
    dateOfBirth: z.date().nullable().optional(),
    gender: z.string().nullable().optional(),
    mainCoachingCategory: z.string().nullable().optional(),
    commonChallenges: z.string().nullable().optional(),
    typicalTimeline: z.string().nullable().optional(),
    typesOfCoaching: z.array(z.string()).nullable().optional(),
    typesOfClients: z.array(z.string()).nullable().optional(),
    typesOfProblems: z.array(z.string()).nullable().optional(),
    coachingFormats: z.array(z.string()).nullable().optional(),
    sessionDeliveryMethods: z.array(z.string()).nullable().optional(),
    experienceLevel: z.string().nullable().optional(),
    coachingApproaches: z.array(z.string()).nullable().optional(),
    faithIntegration: z.array(z.string()).nullable().optional(),
    discType: z.string().nullable().optional(),
    mbtiType: z.string().nullable().optional(),
    enneagramType: z.string().nullable().optional(),
    humanDesignType: z.string().nullable().optional(),
    joinedCommunityAt: z.date().nullable().optional(),
  }),
});

export const updateCoachDetails = createServerFn({ method: "POST" })
  .validator(updateCoachDetailsSchema)
  .handler(async ({ data }) => {
    const { updateCoachDetails: dbUpdateCoachDetails } = await import(
      "~/lib/server/coach"
    );
    return dbUpdateCoachDetails(data.id, data.data);
  });

const ensureCoachDetailsSchema = z.object({
  coachId: z.string(),
  coachDetailsId: z.string().nullable().optional(),
});

export const ensureCoachDetails = createServerFn({ method: "POST" })
  .validator(ensureCoachDetailsSchema)
  .handler(async ({ data }) => {
    const { ensureCoachDetails: dbEnsureCoachDetails } = await import(
      "~/lib/server/coach"
    );
    return dbEnsureCoachDetails(data.coachId, data.coachDetailsId);
  });

const getCoachProfileSchema = z.object({
  id: z.string(),
});

export const getCoachProfile = createServerFn({ method: "GET" })
  .validator(getCoachProfileSchema)
  .handler(async ({ data }) => {
    const { getCoachProfile: dbGetCoachProfile } = await import(
      "~/lib/server/coach"
    );
    return dbGetCoachProfile(data.id);
  });

const updateCoachProfileSchema = z.object({
  id: z.string(),
  profile: z.object({
    isPublic: z.boolean().optional(),
    isPreGeneratedProfile: z.boolean().optional(),
    backgroundInformation: z.string().nullable().optional(),
    brandName: z.string().nullable().optional(),
    brandLogoUrl: z.string().nullable().optional(),
    coachingTitle: z.string().nullable().optional(),
    languages: z.string().nullable().optional(),
    formOfConduct: z.string().nullable().optional(),
    coachingStyle: z.string().nullable().optional(),
    primaryCity: z.string().nullable().optional(),
    primaryCountry: z.string().nullable().optional(),
    closestMajorCity: z.string().nullable().optional(),
    heroImageSrc: z.string().nullable().optional(),
    openingStatement: z.string().nullable().optional(),
    mainImage: z.string().nullable().optional(),
    mainVideo: z.string().nullable().optional(),
    preHeading: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    facebookProfile: z.string().nullable().optional(),
    instagramProfile: z.string().nullable().optional(),
    linkedinProfile: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    youtubeChannel: z.string().nullable().optional(),
    whyTitle: z.string().nullable().optional(),
    whyContent: z.string().nullable().optional(),
    ctaButtonText: z.string().nullable().optional(),
    leadMagnetTitle: z.string().nullable().optional(),
    leadMagnetDescription: z.string().nullable().optional(),
    leadMagnetCtaText: z.string().nullable().optional(),
    leadMagnetUrl: z.string().nullable().optional(),
  }),
});

export const updateCoachProfile = createServerFn({ method: "POST" })
  .validator(updateCoachProfileSchema)
  .handler(async ({ data }) => {
    const { updateCoachProfile: dbUpdateCoachProfile } = await import(
      "~/lib/server/coach"
    );
    return dbUpdateCoachProfile(
      data.id,
      data.profile as Partial<ProfileInsert>,
    );
  });

// ==========================================
// COACH DATA
// ==========================================

const updateCoachSchema = z.object({
  id: z.string(),
  coach: z.object({
    username: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    fullName: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().nullable().optional(),
    profilePictureUrl: z.string().nullable().optional(),
    ghlCalendarId: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    membershipPlan: z.string().optional(),
    mainOfferId: z.string().nullable().optional(),
    profileId: z.string().nullable().optional(),
    coachDetailsId: z.string().nullable().optional(),
    referralCode: z.string().nullable().optional(),
    referredByCoachId: z.string().nullable().optional(),
    stripeConnectOnboardingUrl: z.string().nullable().optional(),
  }),
});

export const updateCoach = createServerFn({ method: "POST" })
  .validator(updateCoachSchema)
  .handler(async ({ data }) => {
    const { updateCoach: dbUpdateCoach } = await import("~/lib/server/coach");
    return dbUpdateCoach(data.id, data.coach as Partial<CoachInsert>);
  });

const getCoachSchema = z.object({
  id: z.string(),
});

export const getCoach = createServerFn({ method: "GET" })
  .validator(getCoachSchema)
  .handler(async ({ data }) => {
    const { getCoach: dbGetCoach } = await import("~/lib/server/coach");
    return dbGetCoach(data.id);
  });

// ==========================================
// OFFERS
// ==========================================

const getOffersByCoachIdSchema = z.object({
  coachId: z.string(),
});

export const getOffersByCoachId = createServerFn({ method: "GET" })
  .validator(getOffersByCoachIdSchema)
  .handler(async ({ data }) => {
    const { getOffersByCoachId: dbGetOffersByCoachId } = await import(
      "~/lib/server/coach"
    );
    return dbGetOffersByCoachId(data.coachId);
  });

const createOfferSchema = z.object({
  offer: z.object({
    coachId: z.string(),
    directSaleEnabled: z.boolean().nullable().optional(),
    price: z.number().min(0).nullable().optional(),
    currency: z.string().length(3).nullable().optional(),
    originalPrice: z.number().min(0).nullable().optional(),
    preHeadline: z.string().nullable().optional(),
    headline: z.string().max(100).nullable().optional(),
    description: z.string().max(300).nullable().optional(),
    vslVideoUrl: z.string().nullable().optional(),
    strugglesHeadline: z.string().nullable().optional(),
    strugglesDescription: z.string().nullable().optional(),
    strugglesImageUrl: z.string().nullable().optional(),
    resultsPreHeadline: z.string().nullable().optional(),
    resultsHeadline: z.string().nullable().optional(),
    resultsDescription: z.string().nullable().optional(),
    resultsImageUrl: z.string().nullable().optional(),
    programHeadline: z.string().nullable().optional(),
    programDescription: z.string().nullable().optional(),
    programImageUrl: z.string().nullable().optional(),
    bottomCtaHeadline: z.string().nullable().optional(),
    bottomCtaText: z.string().nullable().optional(),
    applicationDeadline: z.date().nullable().optional(),
    cohortStartDate: z.date().nullable().optional(),
    satisfactionGuaranteeText: z.string().nullable().optional(),
  }),
});

export const createOffer = createServerFn({ method: "POST" })
  .validator(createOfferSchema)
  .handler(async ({ data }) => {
    const { createOffer: dbCreateOffer } = await import("~/lib/server/coach");
    return dbCreateOffer(data.offer as OfferInsert);
  });

const updateOfferSchema = z.object({
  id: z.string(),
  offer: z.object({
    directSaleEnabled: z.boolean().nullable().optional(),
    price: z.number().min(0).nullable().optional(),
    currency: z.string().length(3).nullable().optional(),
    originalPrice: z.number().min(0).nullable().optional(),
    preHeadline: z.string().nullable().optional(),
    headline: z.string().max(100).nullable().optional(),
    description: z.string().max(300).nullable().optional(),
    vslVideoUrl: z.string().nullable().optional(),
    strugglesHeadline: z.string().nullable().optional(),
    strugglesDescription: z.string().nullable().optional(),
    strugglesImageUrl: z.string().nullable().optional(),
    resultsPreHeadline: z.string().nullable().optional(),
    resultsHeadline: z.string().nullable().optional(),
    resultsDescription: z.string().nullable().optional(),
    resultsImageUrl: z.string().nullable().optional(),
    programHeadline: z.string().nullable().optional(),
    programDescription: z.string().nullable().optional(),
    programImageUrl: z.string().nullable().optional(),
    bottomCtaHeadline: z.string().nullable().optional(),
    bottomCtaText: z.string().nullable().optional(),
    applicationDeadline: z.date().nullable().optional(),
    cohortStartDate: z.date().nullable().optional(),
    satisfactionGuaranteeText: z.string().nullable().optional(),
  }),
});

export const updateOffer = createServerFn({ method: "POST" })
  .validator(updateOfferSchema)
  .handler(async ({ data }) => {
    const { updateOffer: dbUpdateOffer } = await import("~/lib/server/coach");
    return dbUpdateOffer(data.id, data.offer as Partial<OfferInsert>);
  });

const deleteOfferSchema = z.object({
  id: z.string(),
});

export const deleteOffer = createServerFn({ method: "POST" })
  .validator(deleteOfferSchema)
  .handler(async ({ data }) => {
    const { deleteOffer: dbDeleteOffer } = await import("~/lib/server/coach");
    return dbDeleteOffer(data.id);
  });

// ==========================================
// RESOURCES
// ==========================================

const getResourcesByCoachIdSchema = z.object({
  coachId: z.string(),
});

export const getResourcesByCoachId = createServerFn({ method: "GET" })
  .validator(getResourcesByCoachIdSchema)
  .handler(async ({ data }) => {
    const { getResourcesByCoachId: dbGetResourcesByCoachId } = await import(
      "~/lib/server/coach"
    );
    return dbGetResourcesByCoachId(data.coachId);
  });

const createResourceSchema = z.object({
  resource: z.object({
    coachId: z.string(),
    title: z.string(),
    content: z.string(),
    description: z.string(),
    imageSrc: z.string().nullable().optional(),
    publishedAt: z.date().nullable().optional(),
    externalLinkText: z.string().nullable().optional(),
    externalLinkUrl: z.string().nullable().optional(),
  }),
});

export const createResource = createServerFn({ method: "POST" })
  .validator(createResourceSchema)
  .handler(async ({ data }) => {
    const { createResource: dbCreateResource } = await import(
      "~/lib/server/coach"
    );
    return dbCreateResource(data.resource as ResourceInsert);
  });

const updateResourceSchema = z.object({
  id: z.string(),
  resource: z.object({
    title: z.string().optional(),
    content: z.string().optional(),
    description: z.string().optional(),
    imageSrc: z.string().nullable().optional(),
    publishedAt: z.date().nullable().optional(),
    externalLinkText: z.string().nullable().optional(),
    externalLinkUrl: z.string().nullable().optional(),
  }),
});

export const updateResource = createServerFn({ method: "POST" })
  .validator(updateResourceSchema)
  .handler(async ({ data }) => {
    const { updateResource: dbUpdateResource } = await import(
      "~/lib/server/coach"
    );
    return dbUpdateResource(data.id, data.resource as Partial<ResourceInsert>);
  });

const deleteResourceSchema = z.object({
  id: z.string(),
});

export const deleteResource = createServerFn({ method: "POST" })
  .validator(deleteResourceSchema)
  .handler(async ({ data }) => {
    const { deleteResource: dbDeleteResource } = await import(
      "~/lib/server/coach"
    );
    return dbDeleteResource(data.id);
  });
